import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../ui/Button";
import { useToast } from "../../hooks/use-toast";
import {
  CheckCircle2,
  Circle,
  Loader2,
  MinusCircle,
  XCircle,
} from "lucide-react";
import LambdaRunStatus from "../shared/LambdaRunStatus";
import {
  getPatientData,
  getTranscribeJobsByPatient,
  getTranscriptMove,
  getTranscriptMoveHistory,
  previewTranscriptMove,
  regenerateMoveSource,
  regenerateMoveTarget,
  rollbackTranscriptMove,
  startTranscriptMove,
} from "./patientApi";
import {
  buildStepRows,
  canRollbackMove,
  cardProgress,
  defaultCard,
  isCardEnabled,
  STEPPER_STEPS,
  getMoveRuns,
  getNextMoveAction,
  describeMove,
  getSourceMode,
  isMoveUnfinished,
  isMoveGenerating,
  SOURCE_MODES,
} from "./moveTranscriptSteps";
import EmptyNotice from "../shared/EmptyNotice";
import {
  hasSeenMoveGuide,
  markMoveGuideSeen,
} from "./moveTranscriptGuideStorage";
import { isNoteLocked } from "./patientActionConfig";
import { sortTranscriptionJobs } from "./transcriptionJobUtils";

const STEP_ICONS = {
  done: CheckCircle2,
  failed: XCircle,
  running: Loader2,
  undone: MinusCircle,
  skipped: MinusCircle,
  pending: Circle,
};

const STEP_BAR_STYLES = {
  done: "bg-green-600",
  failed: "bg-red-600",
  running: "bg-ktx-purple",
  undone: "bg-amber-600",
  skipped: "bg-gray-400",
  pending: "bg-gray-300",
};

const StepProgressRow = ({ row }) => {
  const Icon = STEP_ICONS[row.status] || Circle;
  return (
    <li
      className={`space-y-1 ${STEP_STYLES[row.status] || STEP_STYLES.pending}`}
    >
      <div className="flex items-center gap-2">
        <Icon
          className={`h-4 w-4 shrink-0 ${row.status === "running" ? "animate-spin" : ""}`}
          aria-hidden
        />
        <span className="tabular-nums w-10 shrink-0">{row.percent}%</span>
        <span>
          {row.label}
          {row.detail ? ` · ${row.detail}` : ""}
          {row.error ? ` · ${row.error}` : ""}
        </span>
      </div>
      <div className="h-1 rounded bg-gray-100 overflow-hidden">
        <div
          role="progressbar"
          aria-label={row.label}
          aria-valuenow={row.percent}
          aria-valuemin={0}
          aria-valuemax={100}
          className={`h-full ${STEP_BAR_STYLES[row.status] || STEP_BAR_STYLES.pending}`}
          style={{ width: `${row.percent}%` }}
        />
      </div>
    </li>
  );
};

const STEP_STYLES = {
  done: "text-green-700",
  failed: "text-red-700",
  running: "text-ktx-purple",
  undone: "text-amber-700",
  skipped: "text-gray-500",
  pending: "text-gray-500",
};

const POLL_INTERVAL_MS = 5000;
const RECENT_RUNS_LIMIT = 2;

const primaryButtonClass =
  "bg-ktx-purple hover:bg-purple-700 text-white shadow-sm";
const disabledButtonClass =
  "bg-gray-200 text-gray-400 shadow-none cursor-not-allowed opacity-100";
const secondaryButtonClass =
  "bg-white border border-ktx-purple text-ktx-purple hover:bg-purple-50 shadow-none";

const Stepper = ({ cards, selected, onSelect, blockedUntil }) => {
  const enabledIndexes = cards
    .map((_, index) => index)
    .filter((index) => isCardEnabled(index, cards) && index >= blockedUntil);
  const handleKeyDown = (event) => {
    const position = enabledIndexes.indexOf(selected);
    const last = enabledIndexes.length - 1;
    const next = {
      ArrowRight: enabledIndexes[Math.min(position + 1, last)],
      ArrowLeft: enabledIndexes[Math.max(position - 1, 0)],
      Home: enabledIndexes[0],
      End: enabledIndexes[last],
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    onSelect(next);
    event.currentTarget.querySelector(`[data-card="${next}"]`)?.focus();
  };
  return (
    <div
      role="tablist"
      aria-label="Move steps"
      className="grid grid-cols-5 gap-2"
      onKeyDown={handleKeyDown}
    >
      {cards.map((card, index) => {
        const Icon = STEP_ICONS[card.status] || Circle;
        const isSelected = index === selected;
        const isEnabled = enabledIndexes.includes(index);
        const isBlocked = index < blockedUntil;
        return (
          <button
            key={card.id}
            type="button"
            role="tab"
            id={`move-card-${card.id}`}
            data-card={index}
            aria-selected={isSelected}
            aria-controls="move-card-panel"
            tabIndex={isSelected ? 0 : -1}
            disabled={!isEnabled}
            onClick={() => onSelect(index)}
            className={`rounded-lg border px-3 py-2 text-left text-xs space-y-1 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-200 ${
              isSelected
                ? "border-ktx-purple bg-purple-50"
                : "border-gray-200 bg-white hover:border-gray-300"
            } ${isEnabled ? "" : "opacity-50 cursor-not-allowed"}`}
          >
            <span
              className={`flex items-center gap-1.5 font-medium ${STEP_STYLES[card.status] || STEP_STYLES.pending}`}
            >
              <Icon
                className={`h-3.5 w-3.5 shrink-0 ${card.status === "running" ? "animate-spin" : ""}`}
                aria-hidden
              />
              <span className={isBlocked ? "line-through" : ""}>
                {index + 1}. {card.label}
              </span>
            </span>
            <span className="block tabular-nums text-gray-600">
              {card.percent}%
            </span>
            <span className="block h-1 rounded bg-gray-100 overflow-hidden">
              <span
                className={`block h-full ${STEP_BAR_STYLES[card.status] || STEP_BAR_STYLES.pending}`}
                style={{ width: `${card.percent}%` }}
              />
            </span>
          </button>
        );
      })}
    </div>
  );
};

const StepRows = ({ rows }) => (
  <ol className="text-sm space-y-2" aria-label="Step progress">
    {rows.map((row) => (
      <StepProgressRow key={row.name} row={row} />
    ))}
  </ol>
);

const MoveSummary = ({ move, part }) => {
  const names = (list) => (list?.length ? list.join(", ") : "none");
  const text = {
    keep: `Source patient ${move.from_patient_id} keeps: ${names(move.from_job_names)}. Source note: ${(move.source_mode || "REGENERATE").toLowerCase()}.`,
    moveTo: `Destination patient ${move.to_patient_id} gets: ${names(move.to_job_names)}.`,
    preview: "The preview was confirmed when the transcripts were replaced.",
  }[part];
  return <EmptyNotice>{text}</EmptyNotice>;
};

const GUIDE_STEPS = [
  "Choose the transcripts that stay with the source patient",
  "Choose the transcript to move and the destination patient",
  "Preview the new transcripts for both patients",
  "Replace the transcripts, then generate the target note",
  "Check the target note, then erase the source patient's note and regenerate it (or set Ready to Record if no transcript is left)",
];

const toJobList = (result) =>
  Array.isArray(result)
    ? sortTranscriptionJobs(result).filter((job) => job.transcribe_job_name)
    : [];

const jobTime = (job) => {
  const time = job.job_start_time || job.upload_date || job.audio_upload_date;
  return time ? new Date(time).toLocaleString() : "Unknown time";
};

const JobChecklist = ({
  jobs,
  selected,
  onToggle,
  disabled,
  emptyText,
  lockedNames = [],
  lockedReason = "",
}) => (
  <div className="max-h-44 overflow-y-auto space-y-2 rounded-md border border-gray-100 p-2">
    {jobs.length === 0 && <EmptyNotice>{emptyText}</EmptyNotice>}
    {jobs.map((job) => {
      const isLocked = lockedNames.includes(job.transcribe_job_name);
      return (
        <label
          key={job.transcribe_job_name}
          className={`flex items-start gap-2 text-xs ${isLocked ? "text-gray-400" : "text-gray-800"}`}
        >
          <input
            type="checkbox"
            className="mt-0.5"
            checked={selected.includes(job.transcribe_job_name)}
            onChange={() => onToggle(job.transcribe_job_name)}
            disabled={disabled || isLocked}
          />
          <span>
            <span className="font-medium">{jobTime(job)}</span>
            {isLocked && <span className="ml-2 italic">{lockedReason}</span>}
            <span className="block font-mono text-[11px] text-gray-500 break-all">
              {job.transcribe_job_name}
            </span>
          </span>
        </label>
      );
    })}
  </div>
);

const Panel = ({ title, hint, action, children }) => (
  <section className="border border-gray-200 rounded-lg p-4 space-y-4">
    <header className="space-y-0.5 pb-3 border-b border-gray-100">
      <div className="flex items-center justify-between gap-2">
        <h5 className="text-sm font-semibold text-gray-900">{title}</h5>
        {action}
      </div>
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </header>
    <div className="space-y-4">{children}</div>
  </section>
);

const TranscriptCompare = ({ title, side }) => (
  <Panel title={`${title} · ${side.description || side.patient_id}`}>
    {side.unchanged ? (
      <EmptyNotice>
        No change. Transcript, note and status stay as they are.
      </EmptyNotice>
    ) : (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {[
            ["Current", side.current_transcript],
            ["New", side.proposed_transcript],
          ].map(([label, text]) => (
            <div key={label}>
              <p className="text-xs font-medium text-gray-700">{label}</p>
              <pre className="whitespace-pre-wrap text-xs bg-gray-50 border border-gray-200 rounded p-2 h-44 overflow-y-auto">
                {text || "-"}
              </pre>
            </div>
          ))}
        </div>
        {side.note_order?.length > 0 && (
          <div className="text-xs text-gray-600">
            <p className="font-medium text-gray-700">
              Note is built in this order
            </p>
            <ol className="list-decimal pl-5 max-h-44 overflow-y-auto">
              {side.note_order.map((job) => (
                <li key={job.transcribe_job_name}>
                  {job.time
                    ? new Date(job.time).toLocaleString()
                    : "Unknown time"}
                  <span className="font-mono text-[11px] text-gray-500">
                    {" "}
                    {job.transcribe_job_name}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </>
    )}
  </Panel>
);

const LastRun = ({ history, patientId }) => {
  if (history.length === 0) return null;
  const [latest, ...rest] = history;
  const older = rest.slice(0, RECENT_RUNS_LIMIT);
  const render = (entry) => {
    const info = describeMove(entry, patientId);
    return (
      <>
        <span className="font-medium">
          {info.status}
          {info.step ? ` at "${info.step}"` : ""}
        </span>{" "}
        by {info.requestedBy}, {info.when}. This patient was the {info.role};{" "}
        {info.role === "source" ? "destination" : "source"}{" "}
        {info.otherPatientId}.
        {info.error && <span className="block text-red-700">{info.error}</span>}
      </>
    );
  };
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-xs text-gray-700 space-y-2">
      <p>
        <span className="font-semibold text-gray-900">Last run: </span>
        {render(latest)}
      </p>
      {older.length > 0 && (
        <details>
          <summary className="cursor-pointer text-gray-600">
            Recent runs ({older.length})
          </summary>
          <ul className="mt-2 space-y-1.5 list-disc pl-5">
            {older.map((entry) => (
              <li key={entry.move_id}>{render(entry)}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

const toggleIn = (list, name) =>
  list.includes(name)
    ? list.filter((entry) => entry !== name)
    : [...list, name];

const MoveTranscriptAction = ({ patient }) => {
  const { toast } = useToast();
  const sourcePatientId = String(patient?.patient_id || "");
  const [isGuideOpen, setIsGuideOpen] = useState(() => !hasSeenMoveGuide());
  const [sourceJobs, setSourceJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [keepNames, setKeepNames] = useState([]);
  const [leaveSourceNote, setLeaveSourceNote] = useState(false);
  const [clearNoteFirst, setClearNoteFirst] = useState(false);
  const [moveNames, setMoveNames] = useState([]);
  const [targetInput, setTargetInput] = useState("");
  const [target, setTarget] = useState(null);
  const [targetJobs, setTargetJobs] = useState([]);
  const [targetKeepNames, setTargetKeepNames] = useState([]);
  const [lookupError, setLookupError] = useState(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [preview, setPreview] = useState(null);
  const [move, setMove] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedCard, setSelectedCard] = useState(0);
  const [blockingMove, setBlockingMove] = useState(null);
  const [isBusy, setIsBusy] = useState(false);

  const sourceLocked = isNoteLocked(patient?.status);
  const targetLocked = target ? isNoteLocked(target.status) : false;
  const bothLocked = sourceLocked && targetLocked;
  const targetJobNames = [...moveNames, ...targetKeepNames];
  const sourceMode = getSourceMode({
    leaveSourceNote: sourceLocked || leaveSourceNote,
    keptCount: keepNames.length,
  });
  const leavesSource = sourceMode === SOURCE_MODES.LEAVE;
  const movesEverything =
    !jobsLoading && sourceJobs.length > 0 && sourceMode === SOURCE_MODES.ERASE;
  const destinationHasNote =
    Boolean(target) &&
    String(target.note || "").trim() !== "" &&
    targetJobs.length > 0;
  const canPreview =
    Boolean(target) &&
    !targetLocked &&
    !blockingMove &&
    targetJobNames.length > 0;
  const moveId = move?.move_id;
  const generating = isMoveGenerating(move);
  const nextAction = getNextMoveAction(move);
  const stepRows = buildStepRows(
    move,
    move?.source_mode || sourceMode,
    move
      ? Boolean(move.clear_note_first)
      : destinationHasNote && clearNoteFirst,
  );
  const clientDone = {
    keep:
      Boolean(move) ||
      (!jobsLoading &&
        sourceJobs.length > 0 &&
        (leavesSource || keepNames.length < sourceJobs.length)),
    moveTo: Boolean(move) || (canPreview && moveNames.length > 0),
    preview: Boolean(move) || Boolean(preview),
  };
  const cards = STEPPER_STEPS.map((card) =>
    cardProgress(card, { rows: stepRows, clientDone: clientDone[card.id] }),
  );
  const currentCard = defaultCard(cards);
  const selected = cards[selectedCard] || cards[0];
  const moveStatus = move?.status;

  useEffect(() => {
    if (moveId) setSelectedCard(currentCard);
  }, [moveId, moveStatus, currentCard]);

  useEffect(() => {
    let cancelled = false;
    setJobsLoading(true);
    getTranscribeJobsByPatient(sourcePatientId).then((result) => {
      if (cancelled) return;
      const jobs = toJobList(result);
      setSourceJobs(jobs);
      setKeepNames(jobs.map((job) => job.transcribe_job_name));
      setMoveNames([]);
      setJobsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [sourcePatientId]);

  const loadHistory = useCallback(async () => {
    const result = await getTranscriptMoveHistory(sourcePatientId);
    const moves = result.moves || [];
    setHistory(moves);
    return moves;
  }, [sourcePatientId]);

  useEffect(() => {
    let cancelled = false;
    setMove(null);
    setSelectedCard(0);
    loadHistory().then(async (moves) => {
      if (cancelled) return;
      const unfinished = moves.find(isMoveUnfinished);
      if (!unfinished) return;
      const loaded = await getTranscriptMove(unfinished.move_id);
      if (cancelled || !loaded.success) return;
      setMove(loaded.move);
    });
    return () => {
      cancelled = true;
    };
  }, [sourcePatientId, loadHistory]);

  useEffect(() => {
    if (move && !isMoveUnfinished(move)) loadHistory();
  }, [move, loadHistory]);

  const openBlockingMove = async () => {
    const loaded = await getTranscriptMove(blockingMove.move_id);
    if (!loaded.success) {
      showError("Could not open the move", loaded);
      return;
    }
    setMove(loaded.move);
    setBlockingMove(null);
  };

  const toggleKeep = (name) => {
    setPreview(null);
    setKeepNames((current) => toggleIn(current, name));
    setMoveNames((current) => current.filter((entry) => entry !== name));
  };

  const toggleMove = (name) => {
    setPreview(null);
    setMoveNames((current) => toggleIn(current, name));
  };

  const toggleTargetKeep = (name) => {
    setPreview(null);
    setTargetKeepNames((current) => toggleIn(current, name));
  };

  const handleLookup = async () => {
    const targetId = targetInput.trim();
    setTarget(null);
    setBlockingMove(null);
    setTargetJobs([]);
    setTargetKeepNames([]);
    setPreview(null);
    setLookupError(null);
    if (!targetId) {
      setLookupError("Destination patient ID is required.");
      return;
    }
    if (targetId === sourcePatientId) {
      setLookupError("Choose a different patient.");
      return;
    }
    setIsLookingUp(true);
    const result = await getPatientData(targetId);
    if (result?.error || !result?.patient_id) {
      setIsLookingUp(false);
      setLookupError(result?.error || `Patient ${targetId} was not found.`);
      return;
    }
    const jobs = toJobList(await getTranscribeJobsByPatient(targetId));
    setIsLookingUp(false);
    setTarget(result);
    setTargetJobs(jobs);
    setTargetKeepNames(jobs.map((job) => job.transcribe_job_name));
    const targetHistory = await getTranscriptMoveHistory(targetId);
    setBlockingMove(
      (targetHistory.moves || []).find(
        (entry) => isMoveUnfinished(entry) && entry.move_id !== moveId,
      ) || null,
    );
  };

  const selection = () => ({
    fromPatientId: sourcePatientId,
    toPatientId: target?.patient_id,
    fromJobNames: leavesSource ? [] : keepNames,
    toJobNames: targetJobNames,
    sourceMode,
    clearNoteFirst: destinationHasNote && clearNoteFirst,
  });

  const showError = (title, result) =>
    toast({ variant: "destructive", title, description: result.error });

  const handlePreview = async () => {
    setIsBusy(true);
    const result = await previewTranscriptMove(selection());
    setIsBusy(false);
    if (!result.success) {
      showError("Could not preview the move", result);
      return false;
    }
    setPreview(result.preview);
    return true;
  };

  const refreshMove = useCallback(async () => {
    if (!moveId) return;
    const result = await getTranscriptMove(moveId);
    if (result.success) setMove(result.move);
  }, [moveId]);

  useEffect(() => {
    if (!moveId || !generating) return undefined;
    let cancelled = false;
    let timer;
    const poll = async () => {
      const result = await getTranscriptMove(moveId).catch(() => null);
      if (!cancelled && result?.success) setMove(result.move);
      if (!cancelled) timer = setTimeout(poll, POLL_INTERVAL_MS);
    };
    timer = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [moveId, generating]);

  const handleReplace = async () => {
    setIsBusy(true);
    const result = await startTranscriptMove(selection());
    if (!result.success) {
      setIsBusy(false);
      showError("Could not replace the transcripts", result);
      if (result.move_id) {
        const failed = await getTranscriptMove(result.move_id);
        if (failed.success) setMove(failed.move);
      }
      return;
    }
    markMoveGuideSeen();
    const loaded = await getTranscriptMove(result.move_id);
    setIsBusy(false);
    if (loaded.success) setMove(loaded.move);
    toast({
      title: "Transcripts replaced",
      description: "Now generate the target patient's note.",
    });
  };

  const runStep = async (action, failureTitle) => {
    setIsBusy(true);
    const result = await action(moveId);
    if (!result.success) showError(failureTitle, result);
    await refreshMove();
    setIsBusy(false);
  };

  const stepButton =
    nextAction === "target"
      ? {
          label: "Generate target note",
          run: () =>
            runStep(regenerateMoveTarget, "Could not generate the target note"),
        }
      : nextAction === "source"
        ? {
            label:
              (move?.source_mode || sourceMode) === SOURCE_MODES.ERASE
                ? "Erase note and set Ready to Record"
                : "Erase and regenerate the source patient's note",
            run: () =>
              runStep(regenerateMoveSource, "Could not generate the note"),
          }
        : null;

  const moveActions = move && (
    <>
      {move.status === "FAILED" && (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 space-y-1"
        >
          <p className="font-semibold">
            This move failed{move.error ? `: ${move.error}` : "."}
          </p>
          <p>
            Retry the step or roll the move back. Patients{" "}
            {move.from_patient_id} and {move.to_patient_id} stay locked for new
            moves until you do.
          </p>
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        {stepButton && (
          <Button
            type="button"
            onClick={stepButton.run}
            disabled={isBusy}
            className={primaryButtonClass}
          >
            {stepButton.label}
          </Button>
        )}
        {canRollbackMove(move) && (
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              runStep(rollbackTranscriptMove, "Could not roll back the move")
            }
            disabled={isBusy}
          >
            Roll back
          </Button>
        )}
      </div>
      {move.status === "TARGET_DONE" && (
        <p className="text-xs text-gray-600">
          The target note is done. Check it, then continue with this
          patient&apos;s note.
        </p>
      )}
      {move.status === "DONE" && (
        <p className="text-xs text-green-700">Move finished.</p>
      )}
      {move.status === "ROLLED_BACK" && (
        <p className="text-xs text-amber-700">Move rolled back.</p>
      )}
    </>
  );
  const showsMoveActions = (cardId) =>
    Boolean(move) && STEPPER_STEPS[currentCard].id === cardId;

  const NEXT_HINTS = {
    keep: 'Untick at least one transcript to move, or tick "Do not touch the source patient\'s note".',
    moveTo:
      "Look up the destination patient and choose at least one transcript to move.",
    preview: "Load the preview to check both transcripts.",
  };

  const selectCard = async (index) => {
    const needsPreview =
      STEPPER_STEPS[index].id === "preview" && !move && !preview;
    if (needsPreview && !(await handlePreview())) return;
    setSelectedCard(index);
  };

  const nextButton = (index) => {
    const card = cards[index];
    const nextCard = cards[index + 1];
    const isReady = card.status === "done";
    return (
      <div className="flex items-center gap-3 flex-wrap border-t border-gray-100 pt-3">
        <Button
          type="button"
          size="sm"
          onClick={() => selectCard(index + 1)}
          disabled={!isReady || isBusy}
          aria-label={`Next: ${nextCard.label}`}
          className={isReady ? primaryButtonClass : disabledButtonClass}
        >
          Next →
        </Button>
        {!isReady && (
          <p className="text-[11px] text-gray-500">{NEXT_HINTS[card.id]}</p>
        )}
      </div>
    );
  };

  const cardBodies = {
    keep: () => (
      <Panel
        title={
          leavesSource
            ? `1. Source patient stays as is (${sourcePatientId})`
            : `1. Source patient: transcripts that stay (${sourcePatientId})`
        }
        hint={
          leavesSource
            ? "Its transcript, note and status are not changed."
            : "Untick a transcript to take it off the source patient, then pick the destination patient in step 2."
        }
      >
        {move ? (
          <MoveSummary move={move} part="keep" />
        ) : (
          <>
            <label className="flex items-start gap-2 text-xs text-gray-800">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={sourceLocked || leaveSourceNote}
                onChange={(event) => {
                  setLeaveSourceNote(event.target.checked);
                  setPreview(null);
                }}
                disabled={isBusy || sourceLocked}
              />
              <span>
                <span className="font-medium">
                  Do not touch the source patient&apos;s note
                </span>
                <span className="block text-gray-500">
                  {sourceLocked
                    ? "This note is Locked, so it can't be changed. The selected transcripts are copied to the destination."
                    : "The selected transcripts are copied to the destination patient. Use this when the note was already fixed."}
                </span>
              </span>
            </label>
            {leavesSource ? null : jobsLoading ? (
              <p className="text-xs text-gray-500">Loading transcripts…</p>
            ) : (
              <JobChecklist
                jobs={sourceJobs}
                selected={keepNames}
                onToggle={toggleKeep}
                disabled={isBusy}
                emptyText="No transcribe jobs for this patient."
              />
            )}
            {movesEverything && (
              <p className="text-[11px] text-amber-700">
                No transcripts stay with the source patient. Their note will be
                erased and their status set to Ready to Record.
              </p>
            )}
          </>
        )}
        {nextButton(0)}
      </Panel>
    ),
    moveTo: () => (
      <Panel
        title="2. Transcript to move and destination patient"
        hint="The selected transcripts are added to the destination patient."
      >
        {move ? (
          <MoveSummary move={move} part="moveTo" />
        ) : (
          <>
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-700">
                {leavesSource
                  ? "Transcript to copy from source patient"
                  : "Transcript to move from source patient"}
              </p>
              {jobsLoading ? (
                <p className="text-xs text-gray-500">Loading transcripts…</p>
              ) : (
                <JobChecklist
                  jobs={sourceJobs}
                  selected={moveNames}
                  onToggle={toggleMove}
                  lockedNames={leavesSource ? [] : keepNames}
                  lockedReason="Stays with the source patient. Untick it in step 1 to move it."
                  disabled={isBusy}
                  emptyText="No transcribe jobs for this patient."
                />
              )}
            </div>
            <div className="rounded-lg border border-gray-300 bg-gray-50 p-3 space-y-1.5">
              <label
                htmlFor="move-transcript-target"
                className="block text-xs font-medium text-gray-700"
              >
                Destination patient ID
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  id="move-transcript-target"
                  aria-label="Destination patient ID"
                  type="text"
                  value={targetInput}
                  onChange={(event) => setTargetInput(event.target.value)}
                  className="border border-gray-300 rounded-md px-2 py-1.5 text-sm w-80"
                  placeholder="Destination patient ID"
                />
                <Button
                  type="button"
                  onClick={handleLookup}
                  disabled={isLookingUp}
                  className={secondaryButtonClass}
                >
                  {isLookingUp ? "Looking up…" : "Look up patient"}
                </Button>
              </div>
            </div>
            {blockingMove && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-2">
                <p>
                  Patient {target?.patient_id || targetInput} cannot be used
                  yet: it is part of an unfinished move ({blockingMove.status})
                  started by {blockingMove.requested_by}.
                </p>
                <Button
                  type="button"
                  onClick={openBlockingMove}
                  className={secondaryButtonClass}
                >
                  Open that move
                </Button>
              </div>
            )}
            {lookupError && (
              <p className="text-xs text-red-600">{lookupError}</p>
            )}
            {target && (
              <div className="text-sm space-y-3">
                <p className="text-gray-700">
                  Patient {target.patient_id} ·{" "}
                  {target.description || "No name"} · MRN {target.mrn || "none"}{" "}
                  · {target.doctor_name || "Unknown doctor"} ·{" "}
                  {target.status || "No status"}
                </p>
                {targetLocked ? (
                  <p className="text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    {bothLocked
                      ? "Both notes are Locked. Nothing can be moved."
                      : "This note is Locked, so its transcript cannot be replaced."}
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-700">
                      Keep the destination patient&apos;s own transcripts
                    </p>
                    <JobChecklist
                      jobs={targetJobs}
                      selected={targetKeepNames}
                      onToggle={toggleTargetKeep}
                      disabled={isBusy}
                      emptyText="The destination patient has no transcribe jobs yet."
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {nextButton(1)}
      </Panel>
    ),
    preview: () => (
      <Panel title="3. Preview the new transcripts">
        {move ? (
          <MoveSummary move={move} part="preview" />
        ) : (
          <>
            {!preview && (
              <p className="text-xs text-gray-500">Loading the preview…</p>
            )}
            {preview && (
              <div className="space-y-2">
                <TranscriptCompare title="Source patient" side={preview.from} />
                <TranscriptCompare
                  title="Destination patient"
                  side={preview.to}
                />
              </div>
            )}
          </>
        )}
        {nextButton(2)}
      </Panel>
    ),
    replace: () => (
      <Panel title="4. Replace the transcripts">
        <StepRows rows={selected.rows} />
        {!move && (
          <>
            {destinationHasNote && (
              <label className="flex items-start gap-2 text-xs text-gray-800">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={clearNoteFirst}
                  onChange={(event) => setClearNoteFirst(event.target.checked)}
                  disabled={isBusy}
                />
                <span>
                  <span className="font-medium">
                    Clear the note first, then rebuild
                  </span>
                  <span className="block text-gray-500">
                    Off by default: the current note stays and each transcript
                    updates it, oldest first. Shown because the destination
                    patient already has a note and a transcript.
                  </span>
                </span>
              </label>
            )}
            <div
              className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-sm space-y-2"
              role="alertdialog"
              aria-label="Confirm replace transcripts"
            >
              <p className="text-gray-800">
                Replacing overwrites the saved transcript for both patients.
                Notes are not changed until the next steps.
              </p>
              <Button
                type="button"
                onClick={handleReplace}
                disabled={isBusy}
                className={primaryButtonClass}
              >
                {isBusy ? "Replacing…" : "Replace transcripts"}
              </Button>
            </div>
          </>
        )}
        {showsMoveActions("replace") && moveActions}
      </Panel>
    ),
    regenerate: () => (
      <Panel
        title="5. Regenerate the notes"
        action={
          move && (
            <LambdaRunStatus
              runs={getMoveRuns(move)}
              title="Note generation logs"
              showBadge={false}
            />
          )
        }
      >
        <StepRows rows={selected.rows} />
        {showsMoveActions("regenerate") && moveActions}
      </Panel>
    ),
  };

  return (
    <div className="space-y-5">
      <LastRun history={history} patientId={sourcePatientId} />
      <details
        open={isGuideOpen}
        onToggle={(event) => setIsGuideOpen(event.currentTarget.open)}
        className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-2 text-xs text-gray-600"
      >
        <summary className="cursor-pointer font-medium text-gray-700">
          How moving a transcript works
        </summary>
        <ol
          className="list-decimal pl-5 mt-2 mb-1 space-y-1"
          aria-label="How moving a transcript works"
        >
          {GUIDE_STEPS.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ol>
      </details>
      <Stepper
        cards={cards}
        selected={selectedCard}
        onSelect={selectCard}
        blockedUntil={!move && !jobsLoading && sourceJobs.length === 0 ? 2 : 0}
      />
      <div
        role="tabpanel"
        id="move-card-panel"
        aria-labelledby={`move-card-${selected.id}`}
      >
        {cardBodies[selected.id]()}
      </div>
    </div>
  );
};

export default MoveTranscriptAction;
